import { Spinner, WidgetSizeEnum } from '@wonderschool/common-base-ui';
// TODO: Lodash should no longer be used here
// eslint-disable-next-line no-restricted-imports
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Navigate, useLocation } from 'react-router-dom';
import {
  getProviderMembershipMarker,
  PROVIDER_MEMBERSHIP_KNOWN_EVENT,
} from '../../../Components/RoleSwitcher/roleContext';
import LoadingComponent from '../../../Components/Shared/LoadingComponent';
import { PaywallModal } from '../../../Components/Shared/PaywallModal';
import { ApolloClientWrapper } from '../../../Components/Wrappers/ApolloClientWrapper';
import WSA from '../../../WSA';
import { getConfig } from '../../../config/env';
import { useOrganizationSetupState } from '../../../hooks/useOrganizations';
import { usePaywallModal } from '../../../hooks/usePaywallModal';
import { usePermissions } from '../../../hooks/useUser';
import { useRouteDebounce, useRoutes } from '../../navigationHooks';
import { paths } from '../../paths';

/** How long to wait for auto-align's reload before giving up and ejecting instead. */
const ALIGN_HOLD_TIMEOUT_MS = 6000;

/**
 * How long to wait for the RoleSwitcher to answer whether this user holds a provider role at all.
 * Much shorter than the align hold — it is one fetch, not a switch plus a reload — and it bounds
 * the wait where nothing will ever answer, like the mobile layout, which has no RoleSwitcher.
 */
const ANSWER_HOLD_TIMEOUT_MS = 1500;

/**
 * Opaque hold screen for the moment between "this user is in a parent context" and the role
 * settling. LoadingComponent is a translucent overlay, so the CCMS admin shell showed through it
 * dimmed — QA read that as a wrong-role flash, pill included (MP-6832).
 *
 * It renders through a portal because no z-index can do this job from where the route lives.
 * Routes render inside `Sidebar.Pusher` (Components/Layout/DesktopContainer.jsx), which carries an
 * inline `transform: translate3d(...)`: a transform makes that element the containing block for
 * `position: fixed` descendants, so `fixed inset-0` covered the pusher's box, not the viewport.
 * `.pushable > .pusher` is also `position: relative; z-index: 2` — a stacking context — while
 * `.ui.sidebar` is `z-index: 102`, so the Director sidebar stayed visible and clickable behind the
 * hold screen whatever we raised it to. document.body is outside both.
 */
const RoleTransitionScreen = () =>
  createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-white" data-testid="role-transition">
      <Spinner size={WidgetSizeEnum.LARGE} />
    </div>,
    document.body
  );

/**
 * The provider-membership marker for `uid`, re-read when the RoleSwitcher writes it. Without the
 * subscription a guard that mounted before the memberships fetch would hold until its timeout even
 * though the answer arrived a few hundred milliseconds in.
 */
function useProviderMembershipMarker(uid?: string) {
  const [marker, setMarker] = useState(() => getProviderMembershipMarker(uid));

  useEffect(() => {
    const reread = () => setMarker(getProviderMembershipMarker(uid));
    // uid can arrive after mount, so re-read on the way in as well as on the event.
    reread();
    window.addEventListener(PROVIDER_MEMBERSHIP_KNOWN_EVENT, reread);
    return () => window.removeEventListener(PROVIDER_MEMBERSHIP_KNOWN_EVENT, reread);
  }, [uid]);

  return marker;
}

export function RestrictedRoute({ route, children }) {
  const {
    isAuthenticated,
    isAuthenticatedProbably,
    didCheck,
    needsVerification,
    userClaims,
    authUser,
    isCCMSParent,
    isCCMSDirector,
    isCCMSStaff,
  } = WSA.Components.useWSAuth();
  const { isRouteActive } = useRoutes();
  const permissions = usePermissions();
  const location = useLocation();
  const { needsOnboarding } = useOrganizationSetupState();
  const { shouldShowPaywall, invoiceUrl } = usePaywallModal();

  const { path } = route;

  // Helper function to create redirect navigation with current location preserved in router state
  const createRedirectNavigation = (targetPath: string) => {
    const currentPath = location.pathname + location.search;
    const existingRedirect = location.state?.redirectTo;

    // If we're on auth pages (signin, verify, completeSignup)
    if ([paths.signin, paths.verify, paths.completeSignup].includes(currentPath)) {
      // Only preserve existing redirect if one exists, don't create a new one
      if (existingRedirect) {
        // Prevent infinite loops by checking if redirect points to the target path
        if (existingRedirect === targetPath) {
          return { to: targetPath };
        }
        return {
          to: targetPath,
          state: { redirectTo: existingRedirect },
        };
      }
      // No existing redirect and we're on auth page - don't add redirect state
      return { to: targetPath };
    }

    // For non-auth pages, always use current path as redirect (existing redirect shouldn't exist here)
    return {
      to: targetPath,
      state: { redirectTo: currentPath },
    };
  };
  const hasUser = !!authUser;
  const hasClaims = !isEmpty(userClaims);
  const hasPermissions = !isEmpty(permissions);
  const hasClaimsAndPermissions = hasClaims && hasPermissions;
  const shouldRenderRoute = isRouteActive(route);

  // special routes where the user state is fluid but we want them to continue
  const isSetupRoute = path === paths.setup;
  const isCompleteSignupRoute = path === paths.completeSignup;
  const isHomeRoute = path === paths.home;
  // /my-roles is not in the routes config, and it is the one page a user stuck in the wrong role
  // must be able to reach — so it is always viewable, never onboarding-gated and never ejected.
  const isMyRolesRoute = path === paths.myRoles;
  const canViewRoute = shouldRenderRoute || isSetupRoute || isHomeRoute || isMyRolesRoute;
  const hasAuthenticated = isAuthenticated && hasUser && hasClaimsAndPermissions;

  const needsDebounce = !hasAuthenticated || needsVerification || isCompleteSignupRoute || !canViewRoute;
  const debounceString = JSON.stringify({
    isAuthenticated,
    isAuthenticatedProbably,
    didCheck,
    needsVerification,
    isCompleteSignupRoute,
    hasClaimsAndPermissions,
    canViewRoute,
    isHomeRoute,
  });

  const isDebounced = useRouteDebounce(debounceString, needsDebounce, 5000);

  // A multi-role user switched to their parent role carries parent-only claims, so claims alone
  // cannot tell them apart from a real parent. The RoleSwitcher records which they are once it has
  // memberships. Users who hold a provider membership are handled by auto-align (it switches them
  // back to their provider role and they stay in CCMS); ejecting them too is what raced auto-align
  // and landed them on the marketing site instead of the parent app (MP-6832).
  //
  // On a cold load or in a new tab the switcher has not answered yet. `unknown` has to hold, not
  // eject: reading it as "no provider role" is why the fix only worked from the second page load
  // in a tab. The switcher answers within one fetch — including a plain "no" when the flag is off
  // — and announces it, so the hold normally ends on the answer, not on a timer.
  const providerMembership = useProviderMembershipMarker(authUser?.uid);

  // Safety net for the hold below: whatever we are waiting for may never arrive — the switch
  // failed, the network is down, or this layout has no RoleSwitcher to answer at all. Fall through
  // to the eject rather than leaving the user on a dead screen.
  const [holdExpired, setHoldExpired] = useState(false);
  const holdTimeout = providerMembership === 'yes' ? ALIGN_HOLD_TIMEOUT_MS : ANSWER_HOLD_TIMEOUT_MS;
  useEffect(() => {
    // Restarting on the answer is deliberate: the align only begins when the marker says "yes".
    const timer = setTimeout(() => setHoldExpired(true), holdTimeout);
    return () => clearTimeout(timer);
  }, [holdTimeout]);

  // Only parent-only users get ejected to the parent app. Claims are the union of a user's roles
  // across all their organizations, so a user can be a parent at one school and staff at another -
  // those users belong in CCMS. isCCMSDirector/isCCMSStaff don't cover moxitAdmin, so check that
  // claim directly.
  const isCCMSStaffOfAnyKind = isCCMSDirector || isCCMSStaff || !!userClaims?.moxitAdmin;

  const willAutoAlign = providerMembership !== 'no' && !holdExpired;

  // /my-roles is where someone switches back out of their parent role, so it never ejects — that
  // would make the only way back unreachable for exactly the people who need it.
  const isParentContextOnly = isAuthenticated && isCCMSParent && !isCCMSStaffOfAnyKind && !isMyRolesRoute;

  // Runs BEFORE the debounce gate: it needs only auth + claims, and waiting out the debounce left
  // seconds of dimmed-but-visible Director chrome on screen before the eject.
  if (isParentContextOnly && !willAutoAlign) {
    // if parents are sent to CCMS /documents, forward them straight to UPE
    // /parents/documents
    const upeDestination = path === paths.documents ? path : '';
    window.location.replace(`${getConfig().wsConfig.parentWebUrl}${upeDestination}`);
    return <RoleTransitionScreen />;
  }

  // Auto-align is in flight (or about to be): hold an opaque screen rather than rendering the
  // admin shell under a translucent spinner, which read as a wrong-role flash during QA.
  if (isParentContextOnly) {
    return <RoleTransitionScreen />;
  }

  if (!isDebounced) {
    return <LoadingComponent />;
  }

  if (isAuthenticated) {
    if (needsVerification) {
      // engage verify user flow
      const navProps = createRedirectNavigation(paths.verify);
      return <Navigate to={navProps.to} state={navProps.state} replace />;
    }

    if (isCompleteSignupRoute) {
      // special case for completing signup
      // the user and might not have claims/permissions yet
      return (
        <ApolloClientWrapper>
          {children}
          <PaywallModal isOpen={shouldShowPaywall} invoiceUrl={invoiceUrl} />
        </ApolloClientWrapper>
      );
    }

    if (needsOnboarding && !isMyRolesRoute) {
      // switch to org setup, complete signup route. A user in their parent role has no CCMS
      // organization, so this would bounce them off /my-roles into provider onboarding.
      const navProps = createRedirectNavigation(paths.completeSignup);
      return <Navigate to={navProps.to} state={navProps.state} replace />;
    }

    if (canViewRoute) {
      // the user can view the route, render it
      return (
        <ApolloClientWrapper>
          {children}
          <PaywallModal isOpen={shouldShowPaywall} invoiceUrl={invoiceUrl} />
        </ApolloClientWrapper>
      );
    }

    return <Navigate to={paths.home} replace />;
  }

  if (!isAuthenticated && !isAuthenticatedProbably && didCheck) {
    // not authenticated, go to signin
    const navProps = createRedirectNavigation(paths.signin);
    return <Navigate to={navProps.to} state={navProps.state} />;
  }

  return <LoadingComponent />;
}
